import { Check, Clock, RefreshCw } from 'lucide-react';
import { cn } from '../cn.js';

export type TimelineState = 'done' | 'active' | 'upcoming';

export interface TimelineItem {
  title: string;
  state: TimelineState;
  /** Who acted, or who it is waiting on. */
  actor?: string | null;
  /** Preformatted — this component does not format dates, so it cannot guess a timezone. */
  at?: string | null;
  note?: string | null;
}

export interface TimelineProps {
  items: TimelineItem[];
}

const STATES: Record<TimelineState, { chip: string; dot: string; label: string }> = {
  done: {
    chip: 'bg-success-subtle text-success',
    dot: 'border-success text-success',
    label: 'Done',
  },
  active: {
    chip: 'bg-accent-subtle text-accent',
    dot: 'border-accent text-accent',
    label: 'In review',
  },
  upcoming: {
    chip: 'bg-bg-subtle text-fg-subtle',
    dot: 'border-border text-fg-subtle',
    label: 'Pending',
  },
};

const ICONS = { done: Check, active: RefreshCw, upcoming: Clock };

/**
 * An approval trail: submitted, verified, audited, disbursed.
 *
 * Generic over its items rather than knowing about claims, because the same read applies to a leave
 * request and (later) an expense advance. Rendered as an ordered list so the sequence is conveyed
 * to a screen reader, not only by the connector line.
 *
 * State is carried by an icon AND a text label, never the dot colour alone — same rule as
 * `statusTone`. The mobile app has a matching component, kept in step by hand.
 */
export function Timeline({ items }: TimelineProps) {
  return (
    <ol className="flex flex-col">
      {items.map((item, index) => {
        const s = STATES[item.state];
        const Icon = ICONS[item.state];
        const isLast = index === items.length - 1;

        return (
          <li key={item.title} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span
                className={cn(
                  'flex size-6 shrink-0 items-center justify-center rounded-full border bg-surface',
                  s.dot,
                )}
              >
                <Icon className="size-3" />
              </span>
              {/* The connector stops at the last step so the trail does not dangle. */}
              {!isLast && <span className="w-px flex-1 bg-border" aria-hidden="true" />}
            </div>

            <div className={cn('min-w-0 flex-1', isLast ? 'pb-0' : 'pb-5')}>
              <div className="flex items-center gap-2">
                <p
                  className={cn(
                    'min-w-0 flex-1 truncate text-sm font-bold',
                    item.state === 'upcoming' ? 'font-semibold text-fg-muted' : 'text-fg',
                  )}
                >
                  {item.title}
                </p>
                <span
                  className={cn(
                    'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                    s.chip,
                  )}
                >
                  {s.label}
                </span>
              </div>

              {(item.at ?? item.actor) && (
                <p className="mt-0.5 truncate text-xs text-fg-muted">
                  {[item.at, item.actor].filter(Boolean).join(' • ')}
                </p>
              )}
              {item.note && <p className="mt-0.5 text-xs italic text-fg-subtle">{item.note}</p>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
